import { FC, useCallback, useContext, useMemo, useRef } from 'react'
import * as React from 'react'
import { ResumeWorkExperience } from '@rio/types'
import { useDispatch } from 'react-redux'
import { useI18n } from 'builder/hooks/useI18n'
import { formatDateRange } from 'builder/utils/formatDateRange'
import { TextField } from 'builder/components/TextField'
import DateRangePicker from 'builder/components/DateRangePicker'
import RichTextArea from 'builder/components/RichTextArea'
import { TunerEditorContext } from 'builder/components/Tuner'
import { AsyncAutosuggest, createSuggestionsApiFetcher } from 'builder/components/AsyncAutosuggest'
import {
  actions,
  getCardFieldHighlightId,
  SectionFieldNames,
  SectionNames,
} from 'builder/modules/resumeEditor'
import { displayFieldPerTemplateConfig } from '../DocumentEditor/ATSResumeEditor/builderSectionsConfig'
import { TemplateName } from '../Helper/constants'
import { WorkExperienceKeywordsToggler } from '../AiFeatures/Keywords'
import { DateRangePickerChangeValue } from '../DateRangePicker/types'
import { RichTextEditorInstanceWrapper } from '../RichTextArea/types'
import { Base } from './Base'

import { CardContentWrapper, Row, Field } from './styles'
import { CardComponentProps } from './types'
import { useWorkExperienceSuggestions } from './hooks/useWorkExperienceSuggestions'

const FieldNames = SectionFieldNames[SectionNames.workExperiences]

export const WorkExperienceCard: FC<CardComponentProps<ResumeWorkExperience>> = props => {
  const {
    item,
    sectionId,
    onChange,
    autoFocus,
    jobTitle,
    template,
    isRecommendedLengthHidden,
    locale,
  } = props
  const { i18n } = useI18n()
  const dispatch = useDispatch()

  const { id: cardId, title, employer } = item

  const cardTitle = useMemo(() => {
    const joiningString = ' ' + i18n.t('builder.resume_editor.labels.at') + ' '

    return (
      /* addind space before and after at */
      [title, employer].filter(Boolean).join(joiningString) ||
      i18n.t('builder.resume_editor.not_specified')
    )
  }, [i18n, employer, title])

  const cardSubtitle = formatDateRange(item)

  // Workaround to perform focusing on the description field from the tooltip
  // TODO: Find a better way
  const tunerEditor = useContext(TunerEditorContext)
  const isActiveIssue = tunerEditor?.activeIssueId === 'no_work_description'

  const editorInstanceRef = useRef<RichTextEditorInstanceWrapper>(null)
  const editorContainerRef = useRef<HTMLDivElement>(null)

  const {
    additionalToolbarActions,
    onFocus,
    shouldAnimateEditorFieldContent,
    isMobileModalScrollDisabled,
  } = useWorkExperienceSuggestions({
    editorInstanceRef,
    editorContainerRef,
    jobTitle: item.title || jobTitle,
    currentWorkExperience: item,
  })

  const isAthensTemplate = template === TemplateName.Athens
  const isPragueTemplate = template === TemplateName.Prague

  const updateSimpleField = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(sectionId, cardId, { [e.target.name]: e.target.value }, true)
    },
    [onChange, sectionId, cardId],
  )

  const updateDescription = useCallback(
    (description: string) => onChange(sectionId, cardId, { description }, true),
    [onChange, sectionId, cardId],
  )

  const updateDateRange = useCallback(
    (value: DateRangePickerChangeValue) => {
      const isChanged = Object.entries(value).some(
        ([key, value]) => value !== item[key as keyof DateRangePickerChangeValue],
      )

      if (isChanged) {
        onChange(sectionId, cardId, value, true)
      }
    },
    [onChange, sectionId, cardId, item],
  )

  const onAiProfileSummaryAnimationEnd = useCallback(() => {
    dispatch(actions.resetAiWorkExperienceFlow())
  }, [dispatch])

  const showDescriptionField = displayFieldPerTemplateConfig(template, sectionId, 'description')

  const showCityField = displayFieldPerTemplateConfig(template, sectionId, 'city')

  const descriptionLabelBadges = [<WorkExperienceKeywordsToggler key="1" />]

  return (
    <Base
      {...props}
      title={cardTitle}
      subtitle={cardSubtitle}
      isMobileModalScrollDisabled={isMobileModalScrollDisabled}
    >
      <CardContentWrapper>
        <Row>
          <Field
            data-highlight-id={getCardFieldHighlightId(
              SectionNames.workExperiences,
              item.cid || item.id,
              FieldNames.title,
            )}
          >
            <TextField
              autoFocus={autoFocus && !isActiveIssue}
              label={i18n.t('builder.resume_editor.work_experiences_card.title')}
              name={FieldNames.title}
              onChange={updateSimpleField}
              value={item.title || ''}
            />
          </Field>

          <Field
            data-highlight-id={getCardFieldHighlightId(
              SectionNames.workExperiences,
              item.cid || item.id,
              FieldNames.employer,
            )}
          >
            <TextField
              label={i18n.t('builder.resume_editor.work_experiences_card.employer')}
              name={FieldNames.employer}
              onChange={updateSimpleField}
              value={item.employer || ''}
            />
          </Field>
        </Row>

        <Row>
          <Field>
            <DateRangePicker
              value={item}
              onChange={updateDateRange}
              currentlyLabel={i18n.t('builder.date_range_picker.currently.work')}
              dateFromHighlightId={getCardFieldHighlightId(
                SectionNames.workExperiences,
                item.cid || item.id,
                FieldNames.dateFrom,
              )}
              dateUntilHighlightId={getCardFieldHighlightId(
                SectionNames.workExperiences,
                item.cid || item.id,
                FieldNames.dateUntil,
              )}
            />
          </Field>
          {showCityField && (
            <Field>
              <AsyncAutosuggest
                highlightedQuery
                name="city"
                value={item.city || ''}
                onChange={updateSimpleField}
                fetchItems={createSuggestionsApiFetcher('city', { locale })}
              >
                {({ key, ...inputProps }) => (
                  <TextField
                    {...inputProps}
                    key={key}
                    label={
                      isAthensTemplate || isPragueTemplate
                        ? i18n.t('builder.resume_editor.work_experiences_card.location')
                        : i18n.t('builder.resume_editor.work_experiences_card.city')
                    }
                  />
                )}
              </AsyncAutosuggest>
            </Field>
          )}
        </Row>

        {showDescriptionField && (
          <Row>
            <Field fullwidth>
              <RichTextArea
                instanceRef={editorInstanceRef}
                containerRef={editorContainerRef}
                autoFocus={autoFocus && isActiveIssue}
                recommendedLength={isRecommendedLengthHidden ? undefined : 200}
                label={i18n.t('builder.resume_editor.work_experiences_card.description')}
                placeholder={i18n.t(
                  'builder.resume_editor.work_experiences_card.description_placeholder',
                )}
                value={item.description || ''}
                onChange={updateDescription}
                locale={locale}
                labelBadges={descriptionLabelBadges}
                additionalToolbarActions={additionalToolbarActions}
                onFocus={onFocus}
                isAnimating={shouldAnimateEditorFieldContent}
                onAnimationEnd={onAiProfileSummaryAnimationEnd}
              />
            </Field>
          </Row>
        )}
      </CardContentWrapper>
    </Base>
  )
}
